// THE BUILD BOARD — the one step a personalized sign-up (POB) meets on the
// welcome's setup surface (components/OnboardingWizard.js;
// functions/onboardingBuild.js tells who, what and when it writes; PLAN §7b-2,
// decision 9 of 2026-10-25: the setup steps show IN the workspace). The
// surface's title says which workspace is set up, its lead says each card is a
// real step, and the step lists the cards: one per server step, each with its
// state (the plain tick once done, a spinner while it runs, a warning when it
// failed, a dash when it was skipped), its name, one result line — a skipped
// step says why — and, when it made something, the way to it. A failed step
// says "Not done" and offers Try again (owner / admin). The way on, "Open my
// workspace", is the surface's foot.
//
// A live region: each card's change is read out as it lands. Kept mounted and
// only hidden (Rule 66): the cards grow in place, the list never jumps. Every
// value comes from functions/onboardingBuild.js through `el.call`; every
// factory names the root key it follows (`…Deps`).

// THE STEP — the cards, then one plain note (a refused Try again, or the poll
// window passed while a step still runs).
export const OnboardingBuildStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'build',
  gap: 'A',

  OnboardingBuildList: {},

  BuildNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z1',
    lineHeight: '1.5',
    color: 'caption',
    overflowWrap: 'break-word',
    hideDeps: [(el, s) => s.root && s.root._pobBuild],
    hide: (el, s) => !(el.call('onboardingBuildView', s.root) || {}).noteText,
    textDeps: [(el, s) => s.root && s.root._pobBuild, (el, s) => s.root && s.root.lang],
    text: (el, s) => (el.call('onboardingBuildView', s.root) || {}).noteText || ''
  }
}

// THE CARDS — one list, one card per server step, in the server's order.
export const OnboardingBuildList = {
  tag: 'ul',
  ariaLive: 'polite',
  margin: '0',
  padding: '0',
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  minWidth: '0',
  childExtends: 'OnboardingBuildCard',
  childrenAs: 'state',
  childrenDeps: [(el, s) => s.root && s.root._pobBuild, (el, s) => s.root && s.root.lang],
  children: (el, s) => (el.call('onboardingBuildView', s.root) || {}).rows || []
}

// ONE CARD — the state mark, then the words: the step's name, its result line
// (a failure in the danger ink), a part that did not go through, and the
// card's actions (Try again; the way to what it made). The card's row carries
// every key it reads, each flag a strict boolean.
export const OnboardingBuildCard = {
  tag: 'li',
  dataBuildStep: (el, s) => (s && s.step) || null,
  dataBuildStatus: (el, s) => (s && s.status) || null,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'Z',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'Z1 A',
  round: 'radiusCard',
  theme: 'layer',
  color: 'title',

  BuildMark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    boxSize: 'avatarXs',
    ariaHidden: 'true',
    Icon: {
      hide: (el, s) => !s || s.running === true,
      name: (el, s) => (s && s.icon) || 'clock',
      boxSize: 'iconSm',
      color: (el, s) =>
        s && s.failed === true ? 'dangerInk' : s && s.done === true ? 'title' : 'caption'
    },
    LoadingSpinner: {
      hide: (el, s) => !s || s.running !== true,
      boxSize: 'iconSm',
      color: 'caption'
    }
  },

  BuildWords: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'X',
    flex: '1 1 auto',
    minWidth: '0',
    BuildName: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      lineHeight: '1.3',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.title) || ''
    },
    BuildResult: {
      tag: 'span',
      fontSize: 'Z1',
      lineHeight: '1.45',
      color: (el, s) => (s && s.failed === true ? 'dangerInk' : 'caption'),
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.resultText) || ''
    },
    BuildPart: {
      tag: 'span',
      fontSize: 'Z1',
      lineHeight: '1.45',
      color: 'caption',
      overflowWrap: 'anywhere',
      hide: (el, s) => !(s && s.partText),
      text: (el, s) => (s && s.partText) || ''
    },
    BuildActions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 'Z',
      minWidth: '0',
      hide: (el, s) => !s || !(s.retryable === true || s.linkHref),
      AppCapsuleBtnGhost: {
        dataBuildAction: 'retry',
        compact: true,
        hide: (el, s) => !s || s.retryable !== true,
        disabled: (el, s) => !!(s && s.retrying === true),
        text: '{{ marketplace.preset.tryAgain | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('onboardingBuildRetry', s && s.step)
        }
      },
      AppCapsuleBtnToolbar: {
        dataBuildAction: 'open',
        compact: true,
        hide: (el, s) => !(s && s.linkHref),
        text: (el, s) => (s && s.linkText) || '',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('onboardingBuildGo', s && s.linkHref)
        }
      }
    }
  }
}
